import { Material } from "three";

export type NodeMaterialLike = Material & {
    isNodeMaterial: true;
}
export function isNodeMaterial(obj: unknown): obj is NodeMaterialLike {
    if (!obj) return false;
    if (typeof obj !== "object") return false;
    const material = obj as NodeMaterialLike;
    return material.isNodeMaterial === true;
}

/**
 * Collects every {@link NodeMaterialLike} reachable from a value: the value
 * itself if it is a node material, otherwise the node materials among its
 * `material` (which may be a single material or an array). Returns `[]` when
 * none are found.
 */
export function getNodeMaterials(target: unknown): NodeMaterialLike[] {
    if (!target) return [];
    if (isNodeMaterial(target)) return [target];
    const material = (target as { material?: unknown }).material;
    if (!material) return [];
    const materials = Array.isArray(material) ? material : [material];
    return materials.filter(isNodeMaterial);
}

/** A NodeMaterial viewed through the fields {@link forceNodeMaterialRecompile} touches. */
export interface MaterialRecompileHandle {
    customProgramCacheKey?: () => string;
    needsUpdate?: boolean;
    __needleRecompileRev?: number;
    __needleRecompileWrapped?: boolean;
}

/**
 * Force a NodeMaterial to rebuild its shader program after a STRUCTURAL edit —
 * one whose value is BAKED into the generated shader rather than uploaded as a
 * live uniform: `OperatorNode.op`, `MathNode.method`, or a `ConstNode.value`.
 *
 * three does NOT include those mutable fields in a node's cache key, so bumping
 * `node.version` / `material.needsUpdate` reuses the cached program (a version
 * bump changes *when* the key is recomputed, not the key itself). Instead we use
 * the material's DOCUMENTED `customProgramCacheKey()` extension point — bumping a
 * revision changes the material's cache key, so the renderer disposes the render
 * object and rebuilds it. The node-build cache is keyed on the render object's
 * cache key, so this also invalidates the built shader and `generate()` re-runs
 * with the new value. No node-internals patching.
 *
 * This is the ONE place that logic lives — both the nodegraph inspection service
 * (structural op/method edits) and the TSL property provider (const-value edits
 * from the Inspector, which may have no captured graph) route through here.
 *
 * @returns the new recompile revision (for diagnostics).
 */
export function forceNodeMaterialRecompile(material: MaterialRecompileHandle): number {
    material.__needleRecompileRev = (material.__needleRecompileRev ?? 0) + 1;
    if (!material.__needleRecompileWrapped) {
        const base = typeof material.customProgramCacheKey === "function"
            ? material.customProgramCacheKey.bind(material)
            : () => "";
        material.customProgramCacheKey = () => `${base()}|needle:${material.__needleRecompileRev ?? 0}`;
        material.__needleRecompileWrapped = true;
    }
    material.needsUpdate = true;
    return material.__needleRecompileRev;
}

export function ignoreNodeMaterialProperty(material: NodeMaterialLike, propertyKey: string): boolean {
    // In node materials some keys exist but aren't used anymore
    // @TODO: We might to check here for the material type in the future
    switch (propertyKey) {
        case "color":
        case "metalness":
        case "roughness":
        case "emissive":
        case "emissiveIntensity":
            return true;
    }

    // Ignore TSL node properties - they're handled by TSL property extraction
    // These properties end with "Node" and contain TSL node graphs
    if (propertyKey.endsWith("Node")) {
        const value = (material as any)[propertyKey];
        if (isNodeLike(value)) {
            return true;
        }
    }

    return false;
}

export interface NodeLike {
    readonly isNode: true;
    name: string;
    needsUpdate: boolean;
    readonly uuid: string;
    readonly version: number;
    /** The node type. This represents the result type of the node (e.g. float or vec3). */
    nodeType: string | null;
    /** The type of the class. The value is usually the constructor name. */
    type: string;
}

export function isNodeLike(obj: unknown): obj is NodeLike {
    if (!obj) return false;
    if (typeof obj !== "object") return false;
    const node = obj as NodeLike;
    return node.isNode === true && typeof node.uuid === "string"
        && typeof node.uuid === "string" && typeof node.version === "number";
}

export interface InputNodeLike extends NodeLike {
    readonly isInputNode: true;
    precision: "lowp" | "mediump" | "highp" | null;
    value: any;
}

export function isInputNode(obj: unknown): obj is InputNodeLike {
    if (!isNodeLike(obj)) return false;
    const node = obj as InputNodeLike;
    return node.isInputNode === true && 'value' in node;
}

export interface VarNodeLike extends NodeLike {
    readonly isVarNode: true;
    intent: boolean;
    global: boolean;
    node: NodeLike;
    readOnly: boolean;
    parents: boolean;
}
export function isVarNode(obj: unknown): obj is VarNodeLike {
    if (!isNodeLike(obj)) return false;
    const node = obj as VarNodeLike;
    return node.isVarNode === true && typeof node.intent === "boolean" && typeof node.global === "boolean" && isNodeLike(node.node) && typeof node.readOnly === "boolean" && typeof node.parents === "boolean";
}

/**
 * Represents a uniform node that can be updated per frame/render/object
 */
export interface UniformNodeLike extends InputNodeLike {
    readonly isUniformNode: true;
}

export function isUniformNode(obj: unknown): obj is UniformNodeLike {
    if (!isInputNode(obj)) return false;
    const node = obj as UniformNodeLike;
    return node.isUniformNode === true;
}

/**
 * Represents a constant node that holds an immutable value
 */
export interface ConstNodeLike extends InputNodeLike {
    readonly isConstNode: true;
}

export function isConstNode(obj: unknown): obj is ConstNodeLike {
    if (!isInputNode(obj)) return false;
    const node = obj as ConstNodeLike;
    return node.isConstNode === true;
}

/**
 * Represents an attribute node that reads from geometry attributes
 */
export interface AttributeNodeLike extends NodeLike {
    readonly isAttributeNode: true;
    attributeName: string;
}

export function isAttributeNode(obj: unknown): obj is AttributeNodeLike {
    if (!isNodeLike(obj)) return false;
    const node = obj as AttributeNodeLike;
    return node.isAttributeNode === true && typeof node.attributeName === "string";
}

/**
 * Represents a varying node for passing data between vertex and fragment shaders
 */
export interface VaryingNodeLike extends NodeLike {
    readonly isVaryingNode: true;
    node: NodeLike;
}

export function isVaryingNode(obj: unknown): obj is VaryingNodeLike {
    if (!isNodeLike(obj)) return false;
    const node = obj as VaryingNodeLike;
    return node.isVaryingNode === true && isNodeLike(node.node);
}

/**
 * Represents a texture sampling node
 */
export interface TextureNodeLike extends NodeLike {
    readonly isTextureNode: true;
    value: unknown;
    uvNode: NodeLike | null;
}

export function isTextureNode(obj: unknown): obj is TextureNodeLike {
    if (!isNodeLike(obj)) return false;
    const node = obj as TextureNodeLike;
    return node.isTextureNode === true && "value" in node;
}

/**
 * Represents an operator node (math operations, etc.)
 */
export interface OperatorNodeLike extends NodeLike {
    readonly isOperatorNode: true;
    op: string;
    aNode: NodeLike;
    bNode: NodeLike | null;
}

export function isOperatorNode(obj: unknown): obj is OperatorNodeLike {
    if (!isNodeLike(obj)) return false;
    const node = obj as OperatorNodeLike;
    return node.isOperatorNode === true && typeof node.op === "string" && isNodeLike(node.aNode);
}

export interface MathNodeLike extends NodeLike {
    readonly isMathNode: true;
    method: string;
    aNode: NodeLike;
    bNode: NodeLike | null;
    cNode: NodeLike | null;
}

export function isMathNode(obj: unknown): obj is MathNodeLike {
    if (!isNodeLike(obj)) return false;
    const node = obj as MathNodeLike;
    return node.isMathNode === true && typeof node.method === "string" && isNodeLike(node.aNode);
}

/**
 * Represents a function node that can execute custom shader functions
 */
export interface FunctionNodeLike extends NodeLike {
    readonly isFunctionNode: true;
}

export function isFunctionNode(obj: unknown): obj is FunctionNodeLike {
    if (!isNodeLike(obj)) return false;
    const node = obj as FunctionNodeLike;
    return node.isFunctionNode === true;
}

export interface PassNodeLike extends NodeLike {
    readonly isPassNode: true;
}

export function isPassNode(obj: unknown): obj is PassNodeLike {
    if (!isNodeLike(obj)) return false;
    const node = obj as PassNodeLike;
    return node.isPassNode === true;
}